import SEO from "@/components/SEO";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Activity,
  Heart,
  Droplet,
  Scale,
  Ruler,
  Eye,
  CheckCircle2,
  Clock,
  Shield,
  Users,
  Building2,
  Phone,
  Mail,
  ChevronRight,
  Star,
  Zap,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import biometricHero from "@/assets/biometric-screening-hero.jpg";

const screeningTests = [
  {
    icon: Activity,
    title: "Blood Pressure",
    description: "Systolic & diastolic measurement with hypertension risk flag",
    duration: "2 min",
  },
  {
    icon: Droplet,
    title: "Blood Glucose",
    description: "Fingerstick fasting or random glucose check for diabetes screening",
    duration: "3 min",
  },
  {
    icon: Heart,
    title: "Cholesterol Panel",
    description: "Total cholesterol, HDL, LDL & triglycerides via fingerstick",
    duration: "5 min",
  },
  {
    icon: Scale,
    title: "BMI & Body Composition",
    description: "Weight, height, body mass index and body fat percentage",
    duration: "3 min",
  },
  {
    icon: Droplet,
    title: "Hemoglobin / A1C",
    description: "Anemia detection & 3-month blood sugar average",
    duration: "5 min",
  },
  {
    icon: Eye,
    title: "Vision Screening",
    description: "Near & far visual acuity screening for workplace compliance",
    duration: "3 min",
  },
  {
    icon: Ruler,
    title: "Waist Circumference",
    description: "Metabolic risk assessment via waist measurement",
    duration: "1 min",
  },
  {
    icon: Activity,
    title: "Pulse Oximetry",
    description: "Blood oxygen saturation & pulse rate via fingertip sensor",
    duration: "1 min",
  },
];

const pricingTiers = [
  {
    name: "Basic Screen",
    price: 29,
    per: "per person",
    highlight: false,
    tests: [
      "Blood Pressure",
      "BMI & Body Composition",
      "Waist Circumference",
      "Pulse Oximetry",
    ],
    cta: "Book Basic",
  },
  {
    name: "Standard Screen",
    price: 49,
    per: "per person",
    highlight: true,
    badge: "Most Popular",
    tests: [
      "Everything in Basic",
      "Blood Glucose (fingerstick)",
      "Total Cholesterol",
      "Hemoglobin",
      "Vision Screening",
    ],
    cta: "Book Standard",
  },
  {
    name: "Comprehensive Screen",
    price: 69,
    per: "per person",
    highlight: false,
    tests: [
      "Everything in Standard",
      "Full Cholesterol Panel (HDL/LDL/Trigs)",
      "Hemoglobin A1C",
      "Screening Report",
      "Personalized Risk Summary",
    ],
    cta: "Book Comprehensive",
  },
];

const employerPackages = [
  { size: "10–24 employees", discount: "10% off per person" },
  { size: "25–49 employees", discount: "15% off per person" },
  { size: "50–99 employees", discount: "20% off per person" },
  { size: "100+ employees", discount: "Custom quote" },
];

const testReferenceData = [
  {
    icon: Activity,
    title: "Blood Pressure",
    importance: "Measures the force of blood against artery walls — a primary indicator of cardiovascular health.",
    normal: {
      value: "< 120 / < 80 mmHg",
      note: "Optimal — heart and arteries are functioning normally.",
    },
    borderline: {
      value: "120–129 / < 80 mmHg",
      note: "Elevated — lifestyle changes recommended to prevent progression.",
    },
    abnormal: {
      value: "≥ 130 / ≥ 80 mmHg",
      note: "High — Stage 1 or Stage 2 hypertension; medical evaluation advised.",
    },
    whyItMatters:
      "Uncontrolled high blood pressure is the leading cause of stroke, heart attack, and kidney failure. Often symptom-free until a critical event occurs.",
  },
  {
    icon: Droplet,
    title: "Blood Glucose (Fasting)",
    importance: "Measures blood sugar levels to screen for prediabetes and Type 2 diabetes.",
    normal: {
      value: "70–99 mg/dL",
      note: "Normal fasting glucose — insulin function is healthy.",
    },
    borderline: {
      value: "100–125 mg/dL",
      note: "Prediabetes — increased risk of developing Type 2 diabetes.",
    },
    abnormal: {
      value: "≥ 126 mg/dL",
      note: "Diabetes range — medical diagnosis and treatment plan required.",
    },
    whyItMatters:
      "Over 96 million American adults have prediabetes and 80% don't know it. Early detection allows lifestyle intervention before irreversible damage occurs.",
  },
  {
    icon: Heart,
    title: "Total Cholesterol",
    importance: "Screens for dyslipidemia — a major risk factor for coronary artery disease and stroke.",
    normal: {
      value: "< 200 mg/dL",
      note: "Desirable — low cardiovascular risk.",
    },
    borderline: {
      value: "200–239 mg/dL",
      note: "Borderline high — diet and activity changes recommended.",
    },
    abnormal: {
      value: "≥ 240 mg/dL",
      note: "High — more than twice the risk of coronary heart disease.",
    },
    whyItMatters:
      "Cholesterol buildup in arteries (atherosclerosis) is silent but deadly. A full panel (HDL/LDL/Triglycerides) reveals the full picture of your heart disease risk.",
  },
  {
    icon: Heart,
    title: "HDL Cholesterol (Good)",
    importance: "HDL removes excess cholesterol from the bloodstream — higher levels are protective.",
    normal: {
      value: "≥ 60 mg/dL",
      note: "Protective — reduces heart disease risk.",
    },
    borderline: {
      value: "41–59 mg/dL",
      note: "Acceptable — room to improve with exercise and diet.",
    },
    abnormal: {
      value: "≤ 40 mg/dL (men) / ≤ 50 mg/dL (women)",
      note: "Low HDL — major risk factor for heart disease.",
    },
    whyItMatters:
      "Low HDL is an independent risk factor for heart attack even when total cholesterol is normal. Exercise, quitting smoking, and healthy fats can raise HDL.",
  },
  {
    icon: Heart,
    title: "LDL Cholesterol (Bad)",
    importance: "LDL deposits cholesterol into artery walls — the primary driver of plaque buildup.",
    normal: {
      value: "< 100 mg/dL",
      note: "Optimal — especially important for those with heart disease risk.",
    },
    borderline: {
      value: "130–159 mg/dL",
      note: "Borderline high — dietary changes and monitoring needed.",
    },
    abnormal: {
      value: "≥ 160 mg/dL",
      note: "High — significantly increases risk of cardiovascular events.",
    },
    whyItMatters:
      "LDL is directly linked to plaque formation in arteries. Statins and lifestyle changes can dramatically lower LDL and reduce the chance of heart attack.",
  },
  {
    icon: Droplet,
    title: "Triglycerides",
    importance: "A type of blood fat elevated by excess sugar, refined carbs, and alcohol intake.",
    normal: {
      value: "< 150 mg/dL",
      note: "Normal — low risk of metabolic syndrome.",
    },
    borderline: {
      value: "150–199 mg/dL",
      note: "Borderline high — diet and lifestyle review recommended.",
    },
    abnormal: {
      value: "≥ 200 mg/dL",
      note: "High — increased risk of pancreatitis and heart disease.",
    },
    whyItMatters:
      "High triglycerides combined with low HDL is a hallmark of metabolic syndrome, strongly associated with Type 2 diabetes and cardiovascular disease.",
  },
  {
    icon: Scale,
    title: "BMI (Body Mass Index)",
    importance: "A ratio of weight to height used to classify body weight categories.",
    normal: {
      value: "18.5 – 24.9",
      note: "Healthy weight — lower risk of weight-related conditions.",
    },
    borderline: {
      value: "25.0 – 29.9",
      note: "Overweight — increased risk of diabetes, hypertension.",
    },
    abnormal: {
      value: "≥ 30",
      note: "Obese — high risk for heart disease, stroke, sleep apnea, and more.",
    },
    whyItMatters:
      "While not a complete health measure, BMI is a proven tool to flag increased risk. Paired with waist circumference, it provides a strong metabolic risk estimate.",
  },
  {
    icon: Ruler,
    title: "Waist Circumference",
    importance: "Central (abdominal) fat is more metabolically dangerous than fat elsewhere in the body.",
    normal: {
      value: "< 40 in (men) / < 35 in (women)",
      note: "Low metabolic risk — healthy fat distribution.",
    },
    borderline: {
      value: "40–45 in (men) / 35–40 in (women)",
      note: "Increased risk — visceral fat may be accumulating.",
    },
    abnormal: {
      value: "> 45 in (men) / > 40 in (women)",
      note: "High risk — strongly linked to Type 2 diabetes and heart disease.",
    },
    whyItMatters:
      "Visceral fat surrounds organs and releases inflammatory chemicals that raise blood pressure, blood sugar, and cholesterol simultaneously.",
  },
  {
    icon: Droplet,
    title: "Hemoglobin A1C",
    importance: "Reflects average blood sugar control over the past 2–3 months — the gold standard for diabetes monitoring.",
    normal: {
      value: "< 5.7%",
      note: "Normal — no significant risk for diabetes.",
    },
    borderline: {
      value: "5.7% – 6.4%",
      note: "Prediabetes — action now can prevent full diabetes.",
    },
    abnormal: {
      value: "≥ 6.5%",
      note: "Diabetes — ongoing monitoring and treatment required.",
    },
    whyItMatters:
      "A single fasting glucose can be affected by recent meals. A1C shows the bigger picture and is used to diagnose and monitor diabetes management over time.",
  },
  {
    icon: Droplet,
    title: "Hemoglobin (Anemia Screen)",
    importance: "Measures the protein in red blood cells that carries oxygen throughout the body.",
    normal: {
      value: "13.5–17.5 g/dL (men) / 12.0–15.5 g/dL (women)",
      note: "Normal oxygen-carrying capacity.",
    },
    borderline: {
      value: "11–13.4 g/dL (men) / 11–11.9 g/dL (women)",
      note: "Mild anemia — fatigue and weakness may be present.",
    },
    abnormal: {
      value: "< 11 g/dL",
      note: "Moderate-to-severe anemia — medical evaluation needed urgently.",
    },
    whyItMatters:
      "Anemia reduces oxygen delivery to tissues, causing fatigue, dizziness, and impaired cognitive function. It may signal iron deficiency, chronic disease, or blood loss.",
  },
  {
    icon: Activity,
    title: "Pulse Oximetry (SpO₂)",
    importance: "Measures blood oxygen saturation — critical for detecting respiratory and cardiac problems.",
    normal: {
      value: "95% – 100%",
      note: "Normal — adequate oxygen delivery to all tissues.",
    },
    borderline: {
      value: "91% – 94%",
      note: "Low-normal — monitor closely, may indicate mild respiratory issue.",
    },
    abnormal: {
      value: "≤ 90%",
      note: "Hypoxemia — urgent medical evaluation required.",
    },
    whyItMatters:
      "Low SpO₂ can signal asthma, COPD, pneumonia, heart failure, or sleep apnea. It is a rapid, non-invasive way to detect life-threatening oxygen shortfalls.",
  },
  {
    icon: Eye,
    title: "Vision Screening",
    importance: "Assesses near and far visual acuity to identify undetected vision impairment.",
    normal: {
      value: "20/20",
      note: "Normal — full visual clarity without correction.",
    },
    borderline: {
      value: "20/40",
      note: "Reduced acuity — glasses or contacts may be needed.",
    },
    abnormal: {
      value: "Worse than 20/40",
      note: "Significant impairment — referral to eye care specialist recommended.",
    },
    whyItMatters:
      "Undetected vision problems reduce workplace safety and quality of life. In many U.S. states, 20/40 vision or better is required for an unrestricted driver's license.",
  },
];

const BiometricScreening = () => {
  const navigate = useNavigate();

  return (
    <>
      <SEO
        title="Rapid Biometric Screening | NearLab Services Houston"
        description="On-site & mobile biometric screening in Houston. Blood pressure, glucose, cholesterol, BMI & more. Corporate wellness events, employer programs, and individual health checks."
      />

      <div className="min-h-screen bg-background">
        {/* ── HERO ─────────────────────────────────────────────────── */}
        <section className="relative min-h-[70vh] flex items-center overflow-hidden">
          {/* Hero background image */}
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url('${biometricHero}')`,
              backgroundSize: "cover",
              backgroundPosition: "center top",
            }}
          />
          {/* Overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-[hsl(var(--nearlab-teal-dark))/90] via-[hsl(var(--nearlab-teal))/75] to-[hsl(var(--nearlab-teal))/30]" />

          <div className="container mx-auto max-w-6xl px-4 relative z-10 py-20">
            <div className="max-w-2xl">
              <div className="flex flex-wrap gap-2 mb-6">
                <Badge className="bg-[hsl(var(--nearlab-gold))] text-[hsl(var(--nearlab-teal-dark))] font-semibold border-0">
                  <Zap className="h-3 w-3 mr-1" /> Rapid Results
                </Badge>
                <Badge variant="outline" className="border-white/40 text-white">
                  CLIA-Waived
                </Badge>
                <Badge variant="outline" className="border-white/40 text-white">
                  OSHA Compliant
                </Badge>
              </div>

              <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight mb-6">
                Rapid Biometric{" "}
                <span className="text-[hsl(var(--nearlab-gold))]">
                  Health Screening
                </span>
              </h1>

              <p className="text-lg text-white/85 mb-8 leading-relaxed">
                Whether you're checking in on your health, running a workplace
                wellness event, or just want to know your numbers — we make it
                fast, easy, and stress-free. Results in{" "}
                <strong className="text-[hsl(var(--nearlab-gold))]">
                  under 15 minutes
                </strong>
                . We come to you, anywhere in Houston and surrounding areas.
              </p>

              <div className="flex flex-wrap gap-4">
                <Button
                  size="lg"
                  onClick={() => navigate("/booking")}
                  className="bg-[hsl(var(--nearlab-gold))] text-[hsl(var(--nearlab-teal-dark))] hover:bg-[hsl(var(--nearlab-gold-dark))] font-bold shadow-lg"
                >
                  Book Your Screening
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white/50 text-white hover:bg-white/10"
                  onClick={() =>
                    window.location.href = "tel:7135104442"
                  }
                >
                  <Phone className="mr-2 h-4 w-4" />
                  713-510-4442
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* ── STAT BAR ─────────────────────────────────────────────── */}
        <section className="bg-[hsl(var(--nearlab-teal-dark))] text-white py-6 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              {[
                { value: "8+", label: "Tests Available" },
                { value: "15 min", label: "Average Time" },
                { value: "CLIA", label: "Certified Labs" },
                { value: "Same-Day", label: "Appointments" },
              ].map((stat) => (
                <div key={stat.label}>
                  <div className="text-3xl font-bold text-[hsl(var(--nearlab-gold))]">
                    {stat.value}
                  </div>
                  <div className="text-sm text-white/70 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── WHAT WE TEST ─────────────────────────────────────────── */}
        <section className="py-16 px-4 bg-[hsl(var(--nearlab-teal-light))/30]">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <Badge variant="outline" className="mb-3 border-[hsl(var(--nearlab-teal))] text-[hsl(var(--nearlab-teal))]">
                Wellness
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-[hsl(var(--nearlab-teal-dark))] mb-4">
                What We Screen For
              </h2>
              <p className="text-muted-foreground max-w-xl mx-auto">
                Everything is quick, low-fuss, and done by real certified health professionals — either at our office or right at your location.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {screeningTests.map((test) => {
                const Icon = test.icon;
                return (
                  <Card
                    key={test.title}
                    className="border-border hover:border-[hsl(var(--nearlab-teal))/40] hover:shadow-md transition-all group"
                  >
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div className="p-2 rounded-lg bg-[hsl(var(--nearlab-teal-light))]">
                          <Icon className="h-5 w-5 text-[hsl(var(--nearlab-teal))]" />
                        </div>
                        <span className="text-xs font-semibold text-[hsl(var(--nearlab-gold-dark))] bg-[hsl(var(--nearlab-gold-light))] px-2 py-1 rounded-full flex items-center gap-1">
                          <Clock className="h-3 w-3" /> {test.duration}
                        </span>
                      </div>
                      <h3 className="font-semibold text-[hsl(var(--nearlab-teal-dark))] mb-1">
                        {test.title}
                      </h3>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {test.description}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── TEST REFERENCE GUIDE ─────────────────────────────────── */}
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <Badge variant="outline" className="mb-3 border-[hsl(var(--nearlab-teal))] text-[hsl(var(--nearlab-teal))]">
                Clinical Reference Guide
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-[hsl(var(--nearlab-teal-dark))] mb-4">
                Understanding Your Results
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Not sure what your results mean? Here's a plain-English breakdown of each test, what's considered normal, and why it matters for your health.
              </p>
            </div>

            <div className="space-y-4">
              {testReferenceData.map((test) => {
                const Icon = test.icon;
                return (
                  <div
                    key={test.title}
                    className="rounded-xl border border-border bg-card overflow-hidden"
                  >
                    {/* Header row */}
                    <div className="flex items-center gap-4 p-5 bg-[hsl(var(--nearlab-teal-light))/40]">
                      <div className="p-2.5 rounded-lg bg-[hsl(var(--nearlab-teal-light))] flex-shrink-0">
                        <Icon className="h-5 w-5 text-[hsl(var(--nearlab-teal))]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-[hsl(var(--nearlab-teal-dark))] text-lg">{test.title}</h3>
                        <p className="text-sm text-muted-foreground mt-0.5">{test.importance}</p>
                      </div>
                    </div>

                    {/* Ranges grid */}
                    <div className="grid sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-border">
                      <div className="p-4 bg-green-50/60">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-2.5 h-2.5 rounded-full bg-green-500 flex-shrink-0" />
                          <span className="text-xs font-bold text-green-700 uppercase tracking-wider">Normal</span>
                        </div>
                        <p className="text-sm font-semibold text-green-800">{test.normal.value}</p>
                        <p className="text-xs text-green-700/80 mt-1">{test.normal.note}</p>
                      </div>
                      <div className="p-4 bg-amber-50/60">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-2.5 h-2.5 rounded-full bg-amber-500 flex-shrink-0" />
                          <span className="text-xs font-bold text-amber-700 uppercase tracking-wider">Borderline</span>
                        </div>
                        <p className="text-sm font-semibold text-amber-800">{test.borderline.value}</p>
                        <p className="text-xs text-amber-700/80 mt-1">{test.borderline.note}</p>
                      </div>
                      <div className="p-4 bg-red-50/60">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-2.5 h-2.5 rounded-full bg-red-500 flex-shrink-0" />
                          <span className="text-xs font-bold text-red-700 uppercase tracking-wider">Abnormal / At Risk</span>
                        </div>
                        <p className="text-sm font-semibold text-red-800">{test.abnormal.value}</p>
                        <p className="text-xs text-red-700/80 mt-1">{test.abnormal.note}</p>
                      </div>
                    </div>

                    {/* Why it matters */}
                    <div className="px-5 py-3 bg-[hsl(var(--nearlab-teal-dark))/5] border-t border-border">
                      <p className="text-xs text-muted-foreground">
                        <span className="font-semibold text-[hsl(var(--nearlab-teal-dark))]">Why it matters: </span>
                        {test.whyItMatters}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            <p className="text-xs text-center text-muted-foreground mt-8">
              * Reference ranges are general guidelines based on standard clinical values. Individual results should always be reviewed by a qualified healthcare provider. These screenings are not a substitute for a full medical diagnosis.
            </p>
            <p className="text-xs text-center text-muted-foreground mt-2">
              † Ranges sourced from the <span className="font-medium">American Heart Association (AHA)</span>, <span className="font-medium">Centers for Disease Control and Prevention (CDC)</span>, <span className="font-medium">American Diabetes Association (ADA)</span>, and <span className="font-medium">National Institutes of Health (NIH)</span> guidelines.
            </p>
          </div>
        </section>

        {/* ── PRICING ──────────────────────────────────────────────── */}
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <Badge variant="outline" className="mb-3 border-[hsl(var(--nearlab-gold))] text-[hsl(var(--nearlab-gold-dark))]">
                Transparent Pricing
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-[hsl(var(--nearlab-teal-dark))] mb-4">
                Individual Screening Packages
              </h2>
              <p className="text-muted-foreground">
                No surprises, no hidden fees. Pick what fits your needs and book in minutes.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {pricingTiers.map((tier) => (
                <Card
                  key={tier.name}
                  className={`relative overflow-hidden transition-all hover:shadow-xl ${
                    tier.highlight
                      ? "border-2 border-[hsl(var(--nearlab-teal))] shadow-lg scale-105"
                      : "border-border"
                  }`}
                >
                  {tier.badge && (
                    <div className="absolute top-0 right-0">
                      <div className="bg-[hsl(var(--nearlab-gold))] text-[hsl(var(--nearlab-teal-dark))] text-xs font-bold px-3 py-1 rounded-bl-lg">
                        <Star className="h-3 w-3 inline mr-1" />
                        {tier.badge}
                      </div>
                    </div>
                  )}
                  <CardHeader className={`pb-4 ${tier.highlight ? "bg-[hsl(var(--nearlab-teal-light))]" : ""}`}>
                    <p className="text-sm font-semibold text-[hsl(var(--nearlab-teal))] uppercase tracking-wider">
                      {tier.name}
                    </p>
                    <div className="flex items-end gap-1 mt-2">
                      <span className="text-4xl font-bold text-[hsl(var(--nearlab-teal-dark))]">
                        ${tier.price}
                      </span>
                      <span className="text-sm text-muted-foreground mb-1">
                        /{tier.per}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <ul className="space-y-3 mb-6">
                      {tier.tests.map((test) => (
                        <li key={test} className="flex items-start gap-2 text-sm">
                          <CheckCircle2 className="h-4 w-4 text-[hsl(var(--nearlab-teal))] mt-0.5 flex-shrink-0" />
                          <span>{test}</span>
                        </li>
                      ))}
                    </ul>
                    <Button
                      className={`w-full ${
                        tier.highlight
                          ? "bg-[hsl(var(--nearlab-teal))] text-white hover:bg-[hsl(var(--nearlab-teal-dark))]"
                          : "variant-outline border-[hsl(var(--nearlab-teal))] text-[hsl(var(--nearlab-teal))]"
                      }`}
                      variant={tier.highlight ? "default" : "outline"}
                      onClick={() => navigate("/booking")}
                    >
                      {tier.cta}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* ── EMPLOYER / GROUP SECTION ─────────────────────────────── */}
        <section className="py-16 px-4 bg-[hsl(var(--nearlab-teal-dark))] text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-5"
            style={{ backgroundImage: "radial-gradient(circle at 20% 50%, hsl(43,82%,48%) 0%, transparent 60%)" }}
          />
          <div className="container mx-auto max-w-6xl relative z-10">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <Badge className="bg-[hsl(var(--nearlab-gold))] text-[hsl(var(--nearlab-teal-dark))] mb-4 border-0">
                  <Building2 className="h-3 w-3 mr-1" /> Employer Programs
                </Badge>
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Corporate Wellness{" "}
                  <span className="text-[hsl(var(--nearlab-gold))]">
                    On-Site Events
                  </span>
                </h2>
                <p className="text-white/80 mb-6 leading-relaxed">
                  We bring everything to your workplace — equipment, trained staff,
                  and easy-to-read reports your employees can actually use. Great for
                  annual wellness days, health fairs, or helping your team lower
                  insurance premiums.
                </p>
                <ul className="space-y-3 mb-8">
                  {[
                    "Dedicated on-site health team",
                    "Private, confidential screening stations",
                    "Same-day printed & digital reports",
                    "ADA & HIPAA compliant process",
                    "Flexible scheduling — weekends available",
                  ].map((item) => (
                    <li key={item} className="flex items-center gap-3 text-white/90">
                      <CheckCircle2 className="h-4 w-4 text-[hsl(var(--nearlab-gold))] flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <div className="flex flex-wrap gap-4">
                  <Button
                    className="bg-[hsl(var(--nearlab-gold))] text-[hsl(var(--nearlab-teal-dark))] hover:bg-[hsl(var(--nearlab-gold-dark))] font-bold"
                    onClick={() => window.location.href = "mailto:info@nearlabservices.com"}
                  >
                    <Mail className="mr-2 h-4 w-4" />
                    Get a Group Quote
                  </Button>
                  <Button
                    variant="outline"
                    className="border-white/40 text-white hover:bg-white/10"
                    onClick={() => window.location.href = "tel:7135104442"}
                  >
                    <Phone className="mr-2 h-4 w-4" />
                    Call Us
                  </Button>
                </div>
              </div>

              <div>
                <div className="bg-white/10 rounded-2xl p-6 backdrop-blur-sm border border-white/20">
                  <div className="flex items-center gap-2 mb-4">
                    <Users className="h-5 w-5 text-[hsl(var(--nearlab-gold))]" />
                    <h3 className="font-semibold text-lg">Group Discounts</h3>
                  </div>
                  <div className="space-y-3">
                    {employerPackages.map((pkg) => (
                      <div
                        key={pkg.size}
                        className="flex items-center justify-between py-3 px-4 rounded-lg bg-white/10 border border-white/10"
                      >
                        <span className="text-white/90 font-medium">{pkg.size}</span>
                        <span className="text-[hsl(var(--nearlab-gold))] font-bold text-sm">
                          {pkg.discount}
                        </span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-white/60 mt-4">
                    * Discounts apply to Standard and Comprehensive packages.
                    Contact us for custom enterprise pricing.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── WHY NEARLAB ──────────────────────────────────────────── */}
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-[hsl(var(--nearlab-teal-dark))] mb-3">
                Why Choose NearLab?
              </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  icon: Zap,
                  title: "Results in 15 Minutes",
                  desc: "No waiting days for lab results. You walk out knowing your numbers — and what they mean.",
                },
                {
                  icon: Shield,
                  title: "Certified & Compliant",
                  desc: "Every test follows CLIA-waived standards, OSHA protocols, and HIPAA guidelines. Your data stays yours.",
                },
                {
                  icon: Users,
                  title: "We Come to You",
                  desc: "Home, office, event — wherever works for you. No commute, no waiting room, no stress.",
                },
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <Card key={item.title} className="text-center border-border hover:shadow-md transition-all">
                    <CardContent className="p-8">
                      <div className="mx-auto w-14 h-14 rounded-full bg-[hsl(var(--nearlab-teal-light))] flex items-center justify-center mb-4">
                        <Icon className="h-7 w-7 text-[hsl(var(--nearlab-teal))]" />
                      </div>
                      <h3 className="text-lg font-bold text-[hsl(var(--nearlab-teal-dark))] mb-2">
                        {item.title}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {item.desc}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── CTA BANNER ───────────────────────────────────────────── */}
        <section className="py-16 px-4 bg-gradient-to-r from-[hsl(var(--nearlab-gold-light))] to-[hsl(var(--nearlab-teal-light))]">
          <div className="container mx-auto max-w-3xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-[hsl(var(--nearlab-teal-dark))] mb-4">
              Ready to Know Your Numbers?
            </h2>
            <p className="text-[hsl(var(--nearlab-teal))] mb-8 text-lg">
              It takes about 15 minutes. Book online right now, or give us a call and we'll take care of the rest.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button
                size="lg"
                className="bg-[hsl(var(--nearlab-teal))] text-white hover:bg-[hsl(var(--nearlab-teal-dark))] font-bold shadow-lg"
                onClick={() => navigate("/booking")}
              >
                Book Biometric Screening
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-[hsl(var(--nearlab-teal))] text-[hsl(var(--nearlab-teal))] hover:bg-[hsl(var(--nearlab-teal-light))]"
                onClick={() => window.location.href = "mailto:info@nearlabservices.com"}
              >
                <Mail className="mr-2 h-4 w-4" />
                Contact for Corporate Events
              </Button>
            </div>
            <p className="mt-6 text-sm text-[hsl(var(--nearlab-teal))/70]">
              Serving Houston · Richmond · Sugar Land · Katy · Pearland and surrounding areas
            </p>
          </div>
        </section>

        {/* Disclaimer */}
        <section className="py-8 px-4 bg-muted/50 border-t border-border">
          <div className="max-w-4xl mx-auto">
            <div className="flex gap-3 items-start rounded-lg border border-border bg-background p-5 shadow-sm">
              <Shield className="h-5 w-5 mt-0.5 shrink-0 text-muted-foreground" />
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                  Informational Disclaimer
                </p>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Your screening results are meant to give you a helpful snapshot of your health — not a diagnosis. Please share your results with your doctor or a healthcare provider who knows your full medical history. If anything looks off, don't wait — reach out to a professional promptly. NearLab does not diagnose, treat, or prescribe.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default BiometricScreening;
